import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient, InAppNotification } from '@rti/shared';
import { useNotificationToast } from './NotificationToastProvider';

const CHANNEL_ROUTES: Record<string, string> = {
  acc_monthly_snapshot: '/snapshot',
  forecast_update: '/forecast',
  devops_ticket_updates: '/system/tickets',
};

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  const hasTimezone = /[zZ]|[+-]\d\d:?\d\d$/.test(dateStr);
  const parsed = new Date(hasTimezone ? dateStr : `${dateStr}Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(parsed);
}

function formatShortDate(dateStr?: string): string {
  if (!dateStr) return '';
  const hasTimezone = /[zZ]|[+-]\d\d:?\d\d$/.test(dateStr);
  const parsed = new Date(hasTimezone ? dateStr : `${dateStr}Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
  }).format(parsed);
}

function getLink(n: InAppNotification): string | null {
  if (n.payload_json?.link) return n.payload_json.link as string;
  return CHANNEL_ROUTES[n.channel_key] ?? null;
}

const NotificationBell: React.FC = () => {
  const { unreadCount, clearUnreadCount } = useNotificationToast();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = async () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) {
      try {
        const data = await apiClient.getInAppNotifications();
        setNotifications(data);
        if (unreadCount > 0) {
          await apiClient.markAllNotificationsRead();
          clearUnreadCount();
        }
      } catch {}
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await apiClient.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch {}
  };

  const handleDeleteAll = async () => {
    try {
      await apiClient.deleteAllNotifications();
      setNotifications([]);
      clearUnreadCount();
    } catch {}
  };

  const handleClick = (n: InAppNotification) => {
    const link = getLink(n);
    setIsOpen(false);
    if (link) navigate(link);
  };

  const badgeLabel = unreadCount > 9 ? '9+' : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg text-rs-off-white hover:bg-rs-jet-light transition-colors"
      >
        <Bell size={20} />
        {badgeLabel && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-medium leading-none">
            {badgeLabel}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-rs-jet border border-rs-jet-light rounded-lg shadow-xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-rs-jet-light">
            <span className="text-sm font-semibold text-rs-off-white">Notifications</span>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <button
                  onClick={handleDeleteAll}
                  title="Delete all"
                  className="text-rs-off-white/50 hover:text-rs-off-white transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-rs-off-white/60 hover:text-rs-off-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-rs-off-white/50">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => {
                const link = getLink(n);
                return (
                  <div
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`group flex items-start gap-3 px-4 py-3 border-b border-rs-jet-light last:border-b-0 hover:bg-rs-jet-light/50 ${link ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className="flex-1 min-w-0 flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        {!n.is_read && (
                          <span className="w-2 h-2 rounded-full bg-rs-green flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-rs-off-white truncate">{n.title}</span>
                      </div>
                      <p className="text-xs text-rs-off-white/70 line-clamp-2">{n.message}</p>
                      <div className="flex items-center gap-3 text-xs text-rs-off-white/40">
                        <span>Alerted: {formatDate(n.created_at)}</span>
                        {n.payload_json?.data_updated_at && (
                          <span>Updated: {formatShortDate(n.payload_json.data_updated_at as string)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, n.id)}
                      title="Delete"
                      className="flex-shrink-0 mt-0.5 text-rs-off-white/0 group-hover:text-rs-off-white/40 hover:!text-rs-off-white transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
